import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { Link } from "react-router-dom";

const Contact = () => {
  const formRef = useRef(null);
  const lenis = useLenis();

  useEffect(() => {
    const w = "https://tally.so/widgets/embed.js";
    const load = () => {
      if (typeof window.Tally !== "undefined") {
        window.Tally.loadEmbeds();
      } else {
        document
          .querySelectorAll("iframe[data-tally-src]:not([src])")
          .forEach((e) => {
            e.src = e.dataset.tallySrc;
          });
      }
    };
    if (typeof window.Tally !== "undefined") {
      load();
    } else if (document.querySelector(`script[src="${w}"]`) == null) {
      const s = document.createElement("script");
      s.src = w;
      s.onload = load;
      s.onerror = load;
      document.body.appendChild(s);
    }
  }, []);

  useEffect(() => {
    const onMessage = (e) => {
      let data = e?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (data?.event === "Tally.FormSubmitted") {
        // Delay so Tally's dynamic resize / confirmation render settles first.
        setTimeout(() => {
          const el = formRef.current;
          if (!el) return;
          if (lenis) {
            lenis.scrollTo(el, { offset: -100, duration: 1.0 });
          } else {
            const y = el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }, 250);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [lenis]);

  return (
    <section className="relative py-24 md:py-32" id="contact" data-testid="contact-section">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <span className="text-terra font-heading text-sm font-semibold tracking-widest uppercase">Get in touch</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-cream">
            Tell us about your restaurant.
          </h2>
          <p className="mt-4 text-sand leading-relaxed">
            Fill out the form and we'll reach out to set up your free demo.
          </p>
        </motion.div>

        <motion.div
          ref={formRef}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="bg-cream border border-line rounded-lg p-4 sm:p-8"
          data-testid="tally-embed-wrapper"
        >
          <iframe
            data-tally-src="https://tally.so/embed/1AYlyl?alignLeft=1&transparentBackground=1&dynamicHeight=1"
            loading="lazy"
            width="100%"
            height="914"
            frameBorder="0"
            marginHeight="0"
            marginWidth="0"
            title="Book Your Free Demo"
          ></iframe>
        </motion.div>

        <p className="mt-6 text-center text-sm text-sand/70" data-testid="form-consent-text">
          By submitting, you agree to our{" "}
          <Link to="/privacy" className="text-sand underline underline-offset-2 hover:text-terra transition-colors" data-testid="consent-privacy-link">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms" className="text-sand underline underline-offset-2 hover:text-terra transition-colors" data-testid="consent-terms-link">
            Terms of Use
          </Link>
          .
        </p>
      </div>
    </section>
  );
};

export default Contact;
